import { useRef, useState, useMemo } from "react";
import SignatureCanvas from "react-signature-canvas";
import { X } from "lucide-react";
import { useSignature, FIELD_TYPES } from "./SignatureContext";

export default function SignatureModal({ blockId, onClose }) {
  const signaturePadRef = useRef(null);
  const {
    blocks,
    setSignatures,
    savedSignature,
    setSavedSignature,
    savedInitial,
    setSavedInitial,
  } = useSignature();

  const block = useMemo(
    () => blocks.find((b) => b.id === blockId),
    [blocks, blockId],
  );

  const fieldType = block?.fieldType;
  const [typedValue, setTypedValue] = useState("");

  if (!block) return null;

  const hasSaved =
    fieldType === FIELD_TYPES.SIGNATURE ? !!savedSignature : !!savedInitial;

  const resetState = () => {
    setTypedValue("");
    signaturePadRef.current?.clear();
    onClose();
  };

  const applySignature = () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) return;

    const dataUrl = signaturePadRef.current.toDataURL();

    if (!savedSignature) setSavedSignature(dataUrl);

    setSignatures((prev) => ({
      ...prev,
      [blockId]: dataUrl,
    }));

    resetState();
  };

  const applySavedSignature = () => {
    setSignatures((prev) => ({
      ...prev,
      [blockId]: savedSignature,
    }));
    resetState();
  };

  const applyInitial = () => {
    if (!typedValue.trim()) return;

    const canvas = document.createElement("canvas");
    canvas.width = 300;
    canvas.height = 100;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#000";
    ctx.font = '600 30px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(typedValue.toUpperCase(), canvas.width / 2, canvas.height / 2);

    const dataUrl = canvas.toDataURL("image/png");

    if (!savedInitial) setSavedInitial(dataUrl);

    setSignatures((prev) => ({
      ...prev,
      [blockId]: dataUrl,
    }));
    resetState();
  };

  const applySavedInitial = () => {
    setSignatures((prev) => ({
      ...prev,
      [blockId]: savedInitial,
    }));
    resetState();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl animate-in fade-in zoom-in-95">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">
            {fieldType === FIELD_TYPES.INITIAL
              ? "Add your initials"
              : "Add your signature"}
          </h3>
          <button onClick={resetState}>
            <X className="h-5 w-5 text-slate-400 hover:text-slate-700" />
          </button>
        </div>

        {fieldType === FIELD_TYPES.SIGNATURE && (
          <>
            {hasSaved && (
              <div className="mb-4">
                <p className="mb-2 text-sm font-medium text-slate-600">
                  Use saved signature
                </p>
                <button
                  onClick={applySavedSignature}
                  className="w-full rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 transition hover:border-emerald-500 hover:bg-emerald-100"
                >
                  <img
                    src={savedSignature}
                    alt="Saved signature"
                    className="mx-auto h-16 object-contain"
                  />
                </button>
                <div className="my-3 flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs text-slate-400">or draw a new one</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
              </div>
            )}
            <div className="mb-4 rounded-lg border border-slate-200 shadow-inner">
              <SignatureCanvas
                ref={signaturePadRef}
                penColor="black"
                canvasProps={{ className: "h-60 w-full bg-transparent" }}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                className="text-sm text-slate-500"
                onClick={() => signaturePadRef.current?.clear()}
              >
                Clear
              </button>
              <button
                onClick={applySignature}
                className="rounded-lg bg-sky-500 px-6 py-2 text-sm text-white hover:bg-sky-600"
              >
                Apply
              </button>
            </div>
          </>
        )}

        {fieldType === FIELD_TYPES.INITIAL && (
          <>
            {hasSaved && (
              <div className="mb-4">
                <p className="mb-2 text-sm font-medium text-slate-600">
                  Use saved initials
                </p>
                <button
                  onClick={applySavedInitial}
                  className="w-full rounded-lg border-2 border-purple-300 bg-purple-50 p-3 transition hover:border-purple-500 hover:bg-purple-100"
                >
                  <img
                    src={savedInitial}
                    alt="Saved initials"
                    className="mx-auto h-16 object-contain"
                  />
                </button>
                <div className="my-3 flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs text-slate-400">or type new ones</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
              </div>
            )}
            <input
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder="Type your initials"
              maxLength={40}
              className="mb-4 w-full rounded-lg border border-slate-300 py-4 text-center text-1xl italic"
              style={{ fontFamily: '"system-ui", cursive' }}
            />
            <div className="flex justify-end gap-3">
              <button
                className="text-sm text-slate-500"
                onClick={() => setTypedValue("")}
              >
                Clear
              </button>
              <button
                onClick={applyInitial}
                disabled={!typedValue.trim()}
                className="rounded-lg bg-sky-500 px-6 py-2 ml-2 text-sm text-white hover:bg-sky-600 disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
